import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

/**
 * The selected version's facts as design v2 draws them under its title (prompt 24 Phase 24.4):
 * `Label: value` pairs separated by thin vertical rules — the same row `EntityHeader` draws for an
 * entity, here for a version inside its card. A definition list, so each pair is read as one fact.
 * Shared by the tarif and the group tarif generation screens.
 */
export function VersionFacts({
  facts,
}: {
  readonly facts: readonly (readonly [label: string, value: ReactNode])[];
}): ReactNode {
  return (
    <Stack
      direction="row"
      component="dl"
      useFlexGap
      spacing={1.5}
      divider={<Divider orientation="vertical" flexItem aria-hidden />}
      sx={{ flexWrap: 'wrap', m: 0, mt: 1, rowGap: 0.5 }}
    >
      {facts.map(([label, value]) => (
        <Box key={label} sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography
            component="dt"
            variant="body2"
            // The colon is drawn, not written: the term stays the label itself.
            sx={{ fontWeight: 600, '&::after': { content: '":"' } }}
          >
            {label}
          </Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>
            {value}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}
